import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const RoleBasedNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userRole, setUserRole] = useState('retail');
  const [showARIndicator, setShowARIndicator] = useState(false);

  useEffect(() => {
    const path = location?.pathname;
    if (path?.includes('retail-user')) setUserRole('retail');
    else if (path?.includes('enterprise-client')) setUserRole('enterprise');
    else if (path?.includes('retail-partner')) setUserRole('partner');
    else if (path?.includes('super-administrator')) setUserRole('admin');
  }, [location?.pathname]);

  const getNavigationItems = () => {
    const baseItems = [
      {
        label: 'Dashboard',
        path: userRole === 'retail' ? '/retail-user-dashboard' :
              userRole === 'enterprise' ? '/enterprise-client-dashboard' :
              userRole === 'partner'? '/retail-partner-dashboard' : '/super-administrator-dashboard',
        icon: 'LayoutDashboard',
        tooltip: 'Your personalized dashboard'
      },
      {
        label: 'Financial Data',
        path: '/financial-data-aggregation',
        icon: 'Database',
        tooltip: 'Account aggregation and transactions'
      },
      {
        label: 'Budget Management',
        path: '/budget-management',
        icon: 'Wallet',
        tooltip: 'Create and manage budgets'
      }
    ];

    return baseItems;
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    const dashboardPath = userRole === 'retail' ? '/retail-user-dashboard' :
                         userRole === 'enterprise' ? '/enterprise-client-dashboard' :
                         userRole === 'partner'? '/retail-partner-dashboard' : '/super-administrator-dashboard';
    navigate(dashboardPath);
  };

  const handleARClick = () => {
    setShowARIndicator(true);
    setTimeout(() => setShowARIndicator(false), 2000);
    // Scroll to AR section on current dashboard
    const arSection = document.querySelector('[class*="gradient-to-br from-"]');
    if (arSection) {
      arSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const navigationItems = getNavigationItems();

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-[1000] bg-white/80 backdrop-blur-xl shadow-elevation-2 border-b border-fintech-blue-100">
        <div className="h-16 mx-auto px-6 flex items-center justify-between max-w-[1440px]">
          <div className="flex items-center gap-3 cursor-pointer" onClick={handleLogoClick}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fintech-blue-500 to-fintech-purple-500 flex items-center justify-center shadow-glow-blue animate-pulse-slow">
              <Icon name="TrendingUp" size={24} color="#FFFFFF" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-fintech-blue-600 to-fintech-purple-600 bg-clip-text text-transparent">FINVISAR</span>
          </div>

          <div className="hidden lg:flex items-center gap-2">
            {navigationItems?.map((item) => (
              <div
                key={item?.path}
                className={`px-4 py-2 rounded-xl text-sm font-medium cursor-pointer transition-all duration-300 ${
                  location?.pathname === item?.path
                    ? 'bg-gradient-to-r from-fintech-blue-500 to-fintech-purple-500 text-white shadow-elevation-2'
                    : 'text-muted-foreground hover:bg-fintech-blue-50 hover:text-fintech-blue-600'
                }`}
                onClick={() => handleNavigation(item?.path)}
                title={item?.tooltip}
              >
                <Icon name={item?.icon} size={18} className="inline-block mr-2" />
                {item?.label}
              </div>
            ))}
            
            {/* AR Access Button */}
            <button
              onClick={handleARClick}
              className="relative ml-2 px-4 py-2 rounded-xl text-sm font-bold bg-gradient-to-r from-fintech-purple-500 via-fintech-blue-500 to-fintech-teal-500 text-white shadow-glow-purple hover:shadow-glow-blue transition-all duration-300 hover:scale-105 flex items-center gap-2 animate-pulse-slow"
            >
              <Icon name="Camera" size={18} className="animate-float" />
              AR Mode
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-fintech-green-500 rounded-full animate-pulse"></span>
            </button>
          </div>

          <div className="flex items-center gap-3">
            <SecurityStatusIndicator />
            <UserContextIndicator userRole={userRole} />
          </div>
        </div>
        
        {/* AR Mode Indicator */}
        {showARIndicator && (
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-4 py-2 bg-gradient-to-r from-fintech-purple-500 to-fintech-blue-500 text-white rounded-xl shadow-elevation-4 text-sm font-medium animate-pulse">
            <Icon name="Camera" size={16} className="inline-block mr-2" />
            Scroll down to access AR features
          </div>
        )}
      </nav>
      <button
        className="role-based-navigation-mobile-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle mobile menu"
      >
        <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
      </button>
      <div className={`role-based-navigation-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="role-based-navigation-mobile-header">
          <div className="role-based-navigation-logo" onClick={handleLogoClick}>
            <div className="role-based-navigation-logo-icon">
              <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
            </div>
            <span className="role-based-navigation-logo-text">FINVISAR</span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 rounded-lg hover:bg-muted"
            aria-label="Close menu"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="role-based-navigation-mobile-items">
          {navigationItems?.map((item) => (
            <div
              key={item?.path}
              className={`role-based-navigation-mobile-item ${location?.pathname === item?.path ? 'active' : ''}`}
              onClick={() => handleNavigation(item?.path)}
            >
              <Icon name={item?.icon} size={20} className="inline-block mr-3" />
              {item?.label}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const UserContextIndicator = ({ userRole }) => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const roleLabels = {
    retail: 'Retail User',
    enterprise: 'Enterprise Client',
    partner: 'Retail Partner',
    admin: 'Super Administrator'
  };

  const userInitials = roleLabels?.[userRole]?.split(' ')?.map(word => word?.[0])?.join('') || 'RU';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleLogout = () => {
    setDropdownOpen(false);
    navigate('/authentication-hub');
  };

  return (
    <div className="user-context-indicator" ref={dropdownRef}>
      <div
        className="user-context-trigger"
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        <div className="user-context-avatar">{userInitials}</div>
        <div className="user-context-info">
          <div className="user-context-name">User Account</div>
          <div className="user-context-role">{roleLabels?.[userRole]}</div>
        </div>
        <Icon name="ChevronDown" size={16} />
      </div>
      <div className={`user-context-dropdown ${dropdownOpen ? 'open' : ''}`}>
        <div className="user-context-dropdown-header">
          <div className="user-context-dropdown-name">User Account</div>
          <div className="user-context-dropdown-email">user@finvisar.com</div>
        </div>

        <div className="user-context-dropdown-items">
          <div className="user-context-dropdown-item">
            <Icon name="User" size={16} />
            <span>Profile</span>
          </div>
          <div className="user-context-dropdown-item">
            <Icon name="Settings" size={16} />
            <span>Settings</span>
          </div>
          <div className="user-context-dropdown-item">
            <Icon name="HelpCircle" size={16} />
            <span>Help & Support</span>
          </div>
          <div className="user-context-dropdown-item danger" onClick={handleLogout}>
            <Icon name="LogOut" size={16} />
            <span>Logout</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const SecurityStatusIndicator = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [securityStatus, setSecurityStatus] = useState('secure');
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event?.key === 'Escape' && modalOpen) {
        setModalOpen(false);
      }
    };

    const handleClickOutside = (event) => {
      if (modalRef?.current && !modalRef?.current?.contains(event?.target)) {
        setModalOpen(false);
      }
    };

    if (modalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [modalOpen]);

  const getStatusColor = () => {
    switch (securityStatus) {
      case 'secure': return 'secure';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'secure';
    }
  };

  return (
    <>
      <div
        className="security-status-indicator"
        onClick={() => setModalOpen(true)}
      >
        <div className={`security-status-dot ${getStatusColor()}`} />
        <span className="security-status-text">Secure</span>
      </div>

      <div className={`security-status-modal ${modalOpen ? 'open' : ''}`}>
        <div className="security-status-overlay" onClick={() => setModalOpen(false)} />
        <div className="security-status-content" ref={modalRef}>
          <div className="security-status-header">
            <h3 className="security-status-title">Security Status</h3>
            <button
              className="security-status-close"
              onClick={() => setModalOpen(false)}
              aria-label="Close"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="security-status-body">
            <div className="security-status-item">
              <div className="security-status-item-icon">
                <Icon name="Shield" size={20} color="var(--color-success)" />
              </div>
              <div className="security-status-item-content">
                <div className="security-status-item-label">Connection Status</div>
                <div className="security-status-item-value">Secure (TLS 1.3)</div>
              </div>
            </div>

            <div className="security-status-item">
              <div className="security-status-item-icon">
                <Icon name="Clock" size={20} color="var(--color-success)" />
              </div>
              <div className="security-status-item-content">
                <div className="security-status-item-label">Session Timeout</div>
                <div className="security-status-item-value">28 minutes remaining</div>
              </div>
            </div>

            <div className="security-status-item">
              <div className="security-status-item-icon">
                <Icon name="Lock" size={20} color="var(--color-success)" />
              </div>
              <div className="security-status-item-content">
                <div className="security-status-item-label">Data Encryption</div>
                <div className="security-status-item-value">AES-256 Active</div>
              </div>
            </div>

            <div className="security-status-item">
              <div className="security-status-item-icon">
                <Icon name="CheckCircle" size={20} color="var(--color-success)" />
              </div>
              <div className="security-status-item-content">
                <div className="security-status-item-label">Compliance Status</div>
                <div className="security-status-item-value">PCI DSS Compliant</div>
              </div>
            </div>

            <div className="security-status-item">
              <div className="security-status-item-icon">
                <Icon name="Activity" size={20} color="var(--color-success)" />
              </div>
              <div className="security-status-item-content">
                <div className="security-status-item-label">Last Activity</div>
                <div className="security-status-item-value">2 minutes ago</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RoleBasedNavigation;